// Resolves which load combination a response diagram is sourced from.
//
// Each SLS check is evaluated only on its code-mandated combination (concrete crack width →
// quasi-permanent for RC / frequent for PT, concrete stress → quasi-permanent, steel stress and
// the deflection/w-curves → characteristic; timber stress → quasi-permanent) and the backend
// zero-fills the diagram on the other combinations, marking applicability with the explicit
// DiagramResponse.Evaluated flag. The resolver honors the user's selection whenever the selected
// combination actually evaluated the check — a genuine zero result stays displayed — and only
// routes to a combination that did evaluate it when the selected one did not. The resolved id is
// first-class: the UI shows it next to the chart so provenance is never silent.

export interface DiagramSourceResolution
{
  combinationId: string
  // True when the resolved combination actually evaluated the check behind the diagram.
  evaluated: boolean
  // True when the diagram comes from a different combination than the user selected.
  redirected: boolean
}

// Only the mm deflection curves were genuinely EMPTY off-combination in pre-flag payloads,
// so point presence is real provenance for them. Legacy UR diagrams were zero-FILLED on every
// combination — point count proves nothing there.
const LEGACY_POINT_PRESENCE_DIAGRAMS: ReadonlySet<string> = new Set(['slsDeflectionInst', 'slsDeflectionFinal'])

export function isDiagramEvaluated(combinationData: any, diagram: string): boolean
{
  const diagramResponse = combinationData?.[diagram]
  if (!diagramResponse)
  {
    return false
  }
  const flag = diagramResponse.evaluated ?? diagramResponse.Evaluated
  if (typeof flag === 'boolean')
  {
    return flag
  }
  // Absent flag = LEGACY payload (producers always set it now). Never infer UR applicability
  // from zero-filled arrays: legacy UR diagrams report not-evaluated, so the UI shows the
  // explicit notice instead of a wrong-source or meaningless zero curve — recalculating the
  // response produces a flagged payload. The w-curves keep the point-presence rule.
  if (!LEGACY_POINT_PRESENCE_DIAGRAMS.has(diagram))
  {
    return false
  }
  return (diagramResponse.count ?? diagramResponse.Count ?? 0) > 0
}

export function resolveDiagramSource(preferredId: string, combinationIds: string[], dataMap: Map<string, any>, diagram: string): DiagramSourceResolution
{
  if (preferredId && isDiagramEvaluated(dataMap.get(preferredId), diagram))
  {
    return { combinationId: preferredId, evaluated: true, redirected: false }
  }

  const evaluatedId = combinationIds.find(id => isDiagramEvaluated(dataMap.get(id), diagram))
  if (evaluatedId)
  {
    return { combinationId: evaluatedId, evaluated: true, redirected: evaluatedId !== preferredId }
  }

  // No combination evaluated this check (e.g. an opt-in limit that was never configured):
  // keep the user's selection and tell the UI the check is not evaluated anywhere.
  return { combinationId: preferredId, evaluated: false, redirected: false }
}
